<template>
  <div class="flex flex-col">
    <h2>Problemas de Pago</h2>

    <!-- Summary Cards -->
    <div v-if="summary" class="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
      <div class="card text-center">
        <p class="text-3xl font-bold text-error-500">{{ summary.total_with_issues }}</p>
        <p class="text-sm text-content-400">Planes con problemas</p>
      </div>
      <div class="card text-center">
        <p class="text-3xl font-bold text-error-600">{{ summary.suspended_plans }}</p>
        <p class="text-sm text-content-400">Planes suspendidos</p>
      </div>
      <div class="card text-center">
        <p class="text-3xl font-bold text-orange-500">{{ summary.critical_issues }}</p>
        <p class="text-sm text-content-400">Problemas criticos</p>
      </div>
      <div class="card text-center">
        <p class="text-3xl font-bold text-yellow-500">{{ summary.warning_issues }}</p>
        <p class="text-sm text-content-400">Advertencias</p>
      </div>
      <div class="card text-center">
        <p class="text-3xl font-bold text-amber-500">{{ summary.cash_renewal_count }}</p>
        <p class="text-sm text-content-400">Efectivo — renovación</p>
      </div>
    </div>

    <!-- Common Errors Section -->
    <div v-if="summary && summary.common_errors && summary.common_errors.length > 0" class="card mt-6">
      <p class="font-semibold mb-2">Errores mas frecuentes (ultimos 30 dias)</p>
      <div class="flex flex-wrap gap-2">
        <div
          v-for="error in summary.common_errors"
          :key="error.error_code"
          class="bg-gray-100 rounded px-3 py-1 text-sm"
        >
          <span class="font-medium">{{ error.error_message_translated || error.error_code }}</span>
          <span class="text-content-400 ml-1">({{ error.count }})</span>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-6 items-start">
      <status-side-nav :options="statusNavOptions" @select="setFilter" />

      <div class="flex-1 min-w-0 w-full">

    <!-- Search -->
    <custom-search
      class="mt-6 w-full sm:w-[500px]"
      placeholder="Buscar por ID de plan o nombre de cliente"
      v-model="searchText"
      @confirm="search"
      @clear="search"
    />

    <!-- Quotes List -->
    <div class="card mt-6">
      <div class="overflow-x-auto">
        <table class="table-auto text-sm mt-2 bg-white w-full">
        <thead class="text-left">
          <tr class="bg-primary-100">
            <th class="px-4 py-3 rounded-l-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Plan</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Cliente</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Estado</th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">
              {{ filter === 'cash' ? 'Ciclos usados' : 'Intentos' }}
            </th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">
              {{ filter === 'cash' ? 'Vencimiento' : 'Ultimo error' }}
            </th>
            <th class="px-4 py-3 text-xs font-semibold text-primary-700 uppercase tracking-wide">Dias restantes</th>
            <th class="px-4 py-3 rounded-r-lg text-xs font-semibold text-primary-700 uppercase tracking-wide">Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="quote in quotes" :key="quote.id" class="table-row">
            <td class="table-cell">
              <router-link
                :to="{ name: 'quotesDetail', params: { id: quote.id } }"
                class="underline system-button"
              >
                #{{ quote.id }}
              </router-link>
            </td>
            <td class="table-cell">
              <router-link
                v-if="quote.client"
                :to="{ name: 'clientsDetail', params: { id: quote.client.id } }"
                class="underline system-button"
              >
                {{ quote.client.name }} {{ quote.client.last_name }}
              </router-link>
            </td>
            <td class="table-cell">
              <span
                v-if="quote.is_cash_plan"
                class="badge badge-warning"
              >
                <span class="badge-dot"></span>
                Efectivo
              </span>
              <status-badge
                v-else-if="quote.status === 'suspended'"
                status="suspended"
                label="Suspendido"
              />
              <status-badge
                v-else-if="quote.payment_retry_count >= 2"
                status="critical"
                label="Critico"
              />
              <status-badge
                v-else
                status="warning"
                label="Advertencia"
              />
            </td>
            <td class="table-cell">
              <template v-if="quote.is_cash_plan">
                {{ quote.cash_cycles_used }} / {{ quote.cash_prepaid_cycles }}
              </template>
              <template v-else>
                {{ quote.payment_retry_count }} intento{{ quote.payment_retry_count !== 1 ? 's' : '' }}
              </template>
            </td>
            <td class="table-cell">
              <span v-if="quote.is_cash_plan" class="text-amber-600 text-xs">
                Bloque agotado<template v-if="quote.cash_block_ends_at"> · {{ formatDate(quote.cash_block_ends_at) }}</template>
              </span>
              <div v-else class="max-w-[260px] whitespace-normal break-words text-error-500 text-xs leading-snug">
                {{ quote.last_error_message || 'Sin error registrado' }}
              </div>
            </td>
            <td class="table-cell">
              <span v-if="quote.is_cash_plan" class="text-amber-600">
                —
              </span>
              <span v-else-if="quote.status === 'suspended'" class="text-error-500">
                Suspendido
              </span>
              <span v-else-if="quote.days_until_suspension !== null" :class="{
                'text-error-500': quote.days_until_suspension <= 1,
                'text-orange-500': quote.days_until_suspension === 2,
                'text-yellow-500': quote.days_until_suspension > 2
              }">
                {{ quote.days_until_suspension }} dia{{ quote.days_until_suspension !== 1 ? 's' : '' }}
              </span>
            </td>
            <td class="table-cell">
              <div class="flex flex-col items-stretch gap-2 min-w-[130px]">
                <router-link
                  v-if="quote.is_cash_plan"
                  :to="{ name: 'quotesDetail', params: { id: quote.id }, query: { action: 'cash-renewal' } }"
                  class="secondary-button !w-full text-center text-xs"
                >
                  Registrar pago en efectivo
                </router-link>
                <button
                  v-if="!quote.is_cash_plan && quote.status !== 'suspended'"
                  class="secondary-button !w-full text-center text-xs"
                  @click="openRetryModal(quote)"
                  :disabled="retryingPayment === quote.id"
                >
                  {{ retryingPayment === quote.id ? 'Procesando...' : 'Reintentar pago' }}
                </button>
                <button
                  v-if="!quote.is_cash_plan && quote.status === 'suspended'"
                  class="secondary-button !w-full text-center text-xs"
                  @click="reactivateQuote(quote)"
                  :disabled="reactivatingQuote === quote.id"
                >
                  {{ reactivatingQuote === quote.id ? 'Procesando...' : 'Reactivar' }}
                </button>
                <router-link
                  :to="{ name: 'quotesDetail', params: { id: quote.id } }"
                  class="secondary-button !w-full text-center text-xs"
                >
                  Ver detalle
                </router-link>
              </div>
            </td>
          </tr>
          <tr v-if="!loading && (!quotes || quotes.length === 0)">
            <td colspan="7" class="table-cell text-center text-content-400">
              No hay planes con problemas de pago
            </td>
          </tr>
        </tbody>
        </table>
      </div>

      <div v-if="loading" class="flex w-full h-full items-center py-8">
        <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
      </div>
    </div>

    <!-- Pagination -->
    <pagination
      :current-page="currentPage"
      :total-pages="totalPages"
      @page-change="onPageChange"
    />

    <!-- Retry Payment Modal -->
    <transition name="modal">
      <confirmation-modal
        v-if="showRetryModal"
        :message="'Estas seguro que deseas reintentar el pago del plan #' + selectedQuote?.id + '?'"
        @close="closeRetryModal"
        @confirm="retryPayment"
      ></confirmation-modal>
    </transition>
      </div>
    </div>
  </div>
</template>

<script>
import api from "@/api";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import Pagination from "@/components/ui/Pagination.vue";
import StatusSideNav from "@/components/ui/StatusSideNav";
import { formatDate } from "@/utils/dateFormatter";

export default {
  name: "PaymentIssues",

  components: {
    ConfirmationModal,
    Pagination,
    StatusSideNav,
  },

  computed: {
    statusNavOptions() {
      return [
        { value: "all", label: "Todos", icon: "📋", activeClass: "text-error-500" },
        { value: "critical", label: "Críticos (2+ intentos)", icon: "🚨", activeClass: "text-orange-500" },
        { value: "warning", label: "Advertencias (1 intento)", icon: "⚠️", activeClass: "text-yellow-500" },
        { value: "cash", label: "Efectivo", icon: "💵", activeClass: "text-amber-500" },
      ].map((option) => ({
        ...option,
        active: this.filter === option.value,
      }));
    },
  },

  data() {
    return {
      quotes: [],
      summary: null,
      loading: false,
      searchText: "",
      currentPage: 1,
      totalPages: 0,
      filter: "all",
      retryingPayment: null,
      reactivatingQuote: null,
      showRetryModal: false,
      selectedQuote: null,
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "paymentIssues");
    this.loadSummary();
    this.loadQuotesWithIssues();
  },

  methods: {
    formatDate,

    setFilter(value) {
      this.filter = value;
      this.currentPage = 1;
      this.loadQuotesWithIssues();
    },

    async loadSummary() {
      try {
        this.summary = await api.paymentAttempts.getSummary();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    async loadQuotesWithIssues() {
      this.loading = true;

      try {
        const params = {
          page: this.currentPage,
          search: this.searchText,
        };

        if (this.filter === "cash") {
          params.type = "cash";
        } else if (this.filter !== "all") {
          // 'critical' / 'warning' are stripe-side severities
          params.type = "stripe";
          params.severity = this.filter;
        }

        const response = await api.paymentAttempts.getQuotesWithIssues(params);
        this.quotes = response.data;
        this.currentPage = response.current_page;
        this.totalPages = response.last_page;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    search() {
      this.currentPage = 1;
      this.loadQuotesWithIssues();
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadQuotesWithIssues();
    },

    openRetryModal(quote) {
      this.selectedQuote = quote;
      this.showRetryModal = true;
    },

    closeRetryModal() {
      this.showRetryModal = false;
      this.selectedQuote = null;
    },

    async retryPayment() {
      if (!this.selectedQuote) return;

      this.showRetryModal = false;
      this.retryingPayment = this.selectedQuote.id;

      try {
        const response = await api.paymentAttempts.retryPayment(this.selectedQuote.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadSummary();
        this.loadQuotesWithIssues();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.retryingPayment = null;
      this.selectedQuote = null;
    },

    async reactivateQuote(quote) {
      this.reactivatingQuote = quote.id;

      try {
        const response = await api.paymentAttempts.reactivateQuote(quote.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadSummary();
        this.loadQuotesWithIssues();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.reactivatingQuote = null;
    },
  },
};
</script>
